/**
 * @fileoverview Public conflict-check vote page (MOR-882, MOR-965, MOR-939)
 *
 * URL: /conflict-check/$token
 * Protection: Public (no auth required) — the opaque 32-byte token in the
 * URL IS the credential (mirrors the calendar-feed pattern). Firm members
 * land here from the conflict-check email and vote yes/no with a reason in
 * one click; votes stream live into the pipeline's conflict-check panel.
 *
 * A member who has already voted sees their recorded vote first (MOR-965) —
 * not the blank form — and can change or withdraw it while the check is
 * open. Every cast/change/withdrawal is kept in the response's history.
 * Documents attached to the check are viewable here via signed URLs
 * (MOR-939), again on the strength of the token.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation } from "convex/react";
import { format } from "date-fns";
import {
  Building2,
  CheckCircle2,
  FileText,
  ShieldAlert,
  ShieldCheck,
  Undo2,
} from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { FunctionReturnType } from "convex/server";

import { Button } from "@/ui/primitives/button";
import { Textarea } from "@/ui/primitives/textarea";
import { Label } from "@/ui/primitives/label";
import { Spinner } from "@/ui/primitives/spinner";
import { ConflictBriefView } from "@/components/pre-appointment/conflict-brief-view";
import { ConflictPartySchedule } from "@/components/pre-appointment/conflict-party-schedule";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";

export const Route = createFileRoute("/conflict-check/$token")({
  component: ConflictVotePage,
});

type VoteData = NonNullable<
  FunctionReturnType<typeof api.preAppointment.conflicts.getByToken>
>;

function ConflictVotePage() {
  const { token } = Route.useParams();
  const data = useQuery(api.preAppointment.conflicts.getByToken, { token });

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted px-4 py-12">
      <div className="w-full max-w-lg rounded-xl border bg-background p-6 shadow-sm">
        {data === undefined ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner className="text-muted-foreground" />
          </div>
        ) : data === null ? (
          <div className="grid gap-2 text-center">
            <ShieldAlert className="mx-auto size-8 text-muted-foreground" />
            <h1 className="text-lg font-semibold">
              This vote link isn't valid
            </h1>
            <p className="text-sm text-muted-foreground">
              The link may have been mistyped or the conflict check removed.
              Check the link in your email, or vote from inside the app.
            </p>
          </div>
        ) : (
          <VoteBody token={token} data={data} />
        )}
      </div>
    </div>
  );
}

function VoteBody({ token, data }: { token: string; data: VoteData }) {
  // The recorded vote comes from the reactive query, so returning to the
  // link shows the status card, not the blank form (MOR-965). `editing`
  // reopens the form to change an existing vote.
  const [editing, setEditing] = useState(false);
  const hasVote = data.myVote !== null;
  const showForm = data.checkStatus === "open" && (!hasVote || editing);

  return (
    <div className="grid gap-5">
      <div className="grid gap-1">
        <div className="flex items-center gap-2">
          <ShieldCheck className="size-5 text-primary" />
          <h1 className="text-lg font-semibold">Conflict check</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          {data.createdByName} at {data.orgName} is checking for conflicts on{" "}
          <strong>{data.entityName}</strong>.
        </p>
        {/* A group check (MOR-1419) lists every entity it covers — the
            heading above carries the collapsed group name. */}
        {data.entityNames.length > 1 && (
          <ul className="flex flex-wrap gap-1.5 pt-1">
            {data.entityNames.map((name, index) => (
              <li
                key={index}
                className="flex items-center gap-1 rounded-md border bg-muted/40 px-1.5 py-0.5 text-xs"
              >
                <Building2 className="size-3 shrink-0 text-muted-foreground" />
                {name}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="grid gap-3 rounded-lg border bg-muted/40 p-3">
        <ConflictBriefView description={data.description} />
        <ConflictPartySchedule
          partyLists={data.partyLists}
          showAttachmentNote
        />
        <VoteAttachments attachments={data.attachments} />
      </div>

      {data.checkStatus === "closed" ? (
        <p className="text-sm text-muted-foreground">
          This conflict check has been closed — votes can no longer be changed.
          {data.myVote && (
            <>
              {" "}
              Your recorded vote:{" "}
              <strong>
                {data.myVote === "yes" ? "conflict flagged" : "no conflict"}
              </strong>
              .
            </>
          )}
        </p>
      ) : showForm ? (
        <VoteForm
          token={token}
          data={data}
          editing={editing}
          onDone={() => setEditing(false)}
        />
      ) : (
        <VoteStatusCard
          token={token}
          data={data}
          onChangeVote={() => setEditing(true)}
        />
      )}

      <VoteHistory history={data.myHistory} />
    </div>
  );
}

/** Documents attached to the check, viewable straight from the token. */
function VoteAttachments({
  attachments,
}: {
  attachments: VoteData["attachments"];
}) {
  if (attachments.length === 0) return null;
  return (
    <div className="grid gap-1.5 border-t pt-2.5">
      <p className="text-xs font-medium text-muted-foreground">
        Attached documents
      </p>
      {attachments.map((attachment, index) => (
        <div key={index} className="flex items-center gap-2 text-sm">
          <FileText className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate">{attachment.fileName}</span>
          {attachment.url && (
            <Button size="sm" variant="outline" className="h-6 px-2" asChild>
              <a href={attachment.url} target="_blank" rel="noreferrer">
                View
              </a>
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The recorded-vote state (MOR-965): what a member who has already voted
 * sees when they come back through the link — their vote, when it was cast,
 * and the actions to change or withdraw it.
 */
function VoteStatusCard({
  token,
  data,
  onChangeVote,
}: {
  token: string;
  data: VoteData;
  onChangeVote: () => void;
}) {
  const withdrawVote = useMutation(
    api.preAppointment.conflicts.withdrawVoteByToken,
  );
  const [withdrawing, setWithdrawing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleWithdraw = async () => {
    setWithdrawing(true);
    setError(null);
    try {
      await withdrawVote({ token });
    } catch (err) {
      setError(extractErrorMessage(err, "Could not withdraw your vote."));
    } finally {
      setWithdrawing(false);
    }
  };

  const flagged = data.myVote === "yes";

  return (
    <div className="grid gap-3">
      <div
        className={cn(
          "grid gap-1 rounded-lg border p-4",
          flagged
            ? "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/40"
            : "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/40",
        )}
      >
        <div className="flex items-center gap-2">
          {flagged ? (
            <ShieldAlert className="size-5 shrink-0 text-red-600 dark:text-red-400" />
          ) : (
            <CheckCircle2 className="size-5 shrink-0 text-green-600 dark:text-green-400" />
          )}
          <p
            className={cn(
              "text-sm font-semibold",
              flagged
                ? "text-red-700 dark:text-red-400"
                : "text-green-700 dark:text-green-400",
            )}
          >
            Your vote is recorded —{" "}
            {flagged ? "conflict flagged" : "no conflict"}
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          {data.myRespondedAt !== null &&
            `Cast ${format(data.myRespondedAt, "d MMM yyyy 'at' h:mmaaa")}. `}
          You don't need to do anything else, {data.voterName}.
        </p>
        {data.myReason && (
          <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">
            “{data.myReason}”
          </p>
        )}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={onChangeVote}>
          Change vote
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="gap-1.5 text-muted-foreground"
          disabled={withdrawing}
          onClick={() => void handleWithdraw()}
        >
          <Undo2 className="size-3.5" />
          {withdrawing ? "Withdrawing…" : "Withdraw vote"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Withdrawing puts you back in the "no response" list, so you'll be
        reminded again while the check stays open. Both are recorded in the
        check's history.
      </p>
    </div>
  );
}

function VoteForm({
  token,
  data,
  editing,
  onDone,
}: {
  token: string;
  data: VoteData;
  editing: boolean;
  onDone: () => void;
}) {
  const submitVote = useMutation(
    api.preAppointment.conflicts.submitVoteByToken,
  );

  const [vote, setVote] = useState<"yes" | "no" | null>(data.myVote);
  // Changing an existing vote starts from the recorded reason.
  const [reason, setReason] = useState(data.myReason ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!vote) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitVote({ token, vote, reason: reason.trim() || undefined });
      // The reactive query flips the page to the recorded-vote card.
      onDone();
    } catch (err) {
      setError(extractErrorMessage(err, "Could not record your vote."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="grid gap-2">
        <p className="text-sm font-medium">
          {data.voterName}, do you (or your engagements) have a conflict with
          these parties?
        </p>
        {editing && data.myVote && (
          <p className="text-xs text-muted-foreground">
            You're changing your recorded vote (
            {data.myVote === "yes" ? "conflict flagged" : "no conflict"}).
            Submitting replaces it.
          </p>
        )}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setVote("no")}
            className={cn(
              "flex items-center justify-center gap-2 rounded-lg border p-3 text-sm font-medium transition-colors",
              vote === "no"
                ? "border-green-600 bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-400"
                : "hover:bg-muted",
            )}
          >
            <CheckCircle2 className="size-4" />
            No conflict
          </button>
          <button
            type="button"
            onClick={() => setVote("yes")}
            className={cn(
              "flex items-center justify-center gap-2 rounded-lg border p-3 text-sm font-medium transition-colors",
              vote === "yes"
                ? "border-red-600 bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400"
                : "hover:bg-muted",
            )}
          >
            <ShieldAlert className="size-4" />
            Yes, conflict
          </button>
        </div>
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="vote-reason">
          Reason{" "}
          <span className="font-normal text-muted-foreground">
            {vote === "yes"
              ? "(required — describe the conflict)"
              : "(optional)"}
          </span>
        </Label>
        <Textarea
          id="vote-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={
            vote === "yes"
              ? "e.g. I acted for one of the directors in 2024…"
              : "Anything worth noting"
          }
          rows={3}
        />
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex items-center gap-2">
        <Button
          className="flex-1"
          disabled={
            submitting || vote === null || (vote === "yes" && !reason.trim())
          }
          onClick={() => void handleSubmit()}
        >
          {submitting ? "Submitting…" : "Submit vote"}
        </Button>
        {editing && (
          <Button variant="ghost" disabled={submitting} onClick={onDone}>
            Cancel
          </Button>
        )}
      </div>
    </>
  );
}

/** My own vote events, newest first, so the trail is visible to me too. */
function VoteHistory({ history }: { history: VoteData["myHistory"] }) {
  if (history.length === 0) return null;
  return (
    <div className="grid gap-1 border-t pt-3">
      <p className="text-xs font-medium text-muted-foreground">Your history</p>
      <ul className="grid gap-0.5">
        {[...history].reverse().map((entry, index) => (
          <li key={index} className="text-xs text-muted-foreground">
            {entry.action === "withdrawn"
              ? "Withdrew vote"
              : entry.vote === "yes"
                ? "Voted — conflict flagged"
                : "Voted — no conflict"}
            {" · "}
            {format(entry.at, "d MMM yyyy, h:mmaaa")}
          </li>
        ))}
      </ul>
    </div>
  );
}
